import { useState, useEffect } from 'react';
import { FaWhatsapp, FaEnvelope, FaPhone, FaComments, FaTimes, FaMapMarkerAlt, FaClock } from 'react-icons/fa';
import { useContactData } from '../hooks/useTranslations';
import { useTheme } from '../contexts/ThemeContext';
import contactConfig from '../config/contact';

const FloatingContactWidget = () => {
    const [isOpen, setIsOpen] = useState(false);
    const [isVisible, setIsVisible] = useState(false);
    const [shouldPulse, setShouldPulse] = useState(false);
    const contactData = useContactData();
    const { isDark } = useTheme();

    // Extraer las traducciones del floating contact
    const t = contactData.floating || {};

    // Aparecer después de 3 segundos y activar pulso constantemente cada minuto
    useEffect(() => {
        const visibilityTimer = setTimeout(() => {
            setIsVisible(true);
        }, 3000);

        return () => clearTimeout(visibilityTimer);
    }, []);

    // Efecto separado para manejar el pulso constantemente cada minuto
    useEffect(() => {
        if (!isVisible) return;

        // Función para activar pulso
        const activatePulse = () => {
            if (!isOpen) {
                setShouldPulse(true);
                // Detener pulso después de 4 segundos
                setTimeout(() => setShouldPulse(false), 4000);
            }
        };

        // Iniciar primer pulso después de 2 segundos de aparecer
        const initialPulseTimer = setTimeout(activatePulse, 2000);

        // Pulso cada minuto para llamar la atención continuamente
        const pulseInterval = setInterval(activatePulse, 60000); // Cada 60 segundos

        return () => {
            clearTimeout(initialPulseTimer);
            clearInterval(pulseInterval);
        };
    }, [isVisible, isOpen]);

    const contactOptions = [
        {
            icon: FaWhatsapp,
            label: t.whatsappAction || 'Send WhatsApp',
            info: contactConfig.phone.display,
            action: () => window.open(`${contactConfig.phone.whatsapp}?text=${encodeURIComponent(contactConfig.social.whatsappMessage)}`, '_blank'),
            bgColor: isDark
                ? 'bg-gradient-to-r from-green-600 to-green-700 hover:from-green-500 hover:to-green-600'
                : 'bg-gradient-to-r from-green-500 to-green-600 hover:from-green-400 hover:to-green-500',
            iconColor: 'text-white'
        },
        {
            icon: FaEnvelope,
            label: t.emailAction || 'Send Email',
            info: contactConfig.email.address,
            action: () => window.open(`${contactConfig.email.mailto}?subject=${encodeURIComponent(contactConfig.social.emailSubject)}&body=${encodeURIComponent(contactConfig.social.emailBody)}`, '_blank'),
            bgColor: isDark
                ? 'bg-gradient-to-r from-blue-600 to-blue-700 hover:from-blue-500 hover:to-blue-600'
                : 'bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-400 hover:to-blue-500',
            iconColor: 'text-white'
        },
        {
            icon: FaPhone,
            label: t.phoneAction || 'Call Now',
            info: contactConfig.phone.display,
            action: () => window.open(contactConfig.phone.tel, '_blank'),
            bgColor: isDark
                ? 'bg-gradient-to-r from-atlantis to-vida-loca hover:from-green-yellow-primary hover:to-atlantis'
                : 'bg-gradient-to-r from-atlantis to-green-yellow-primary hover:from-vida-loca hover:to-atlantis',
            iconColor: 'text-white'
        }
    ];

    const toggleWidget = () => {
        setIsOpen(!isOpen);
        setShouldPulse(false);
    };

    if (!isVisible) return null;

    return (
        <div className="floating-contact-widget fixed bottom-6 right-6 z-50 group">
            {/* Botón principal */}
            <div className="relative">
                {/* Efecto de pulso del botón principal cuando está cerrado */}
                {!isOpen && shouldPulse && (
                    <div className="absolute inset-0 w-16 h-16 rounded-full bg-gradient-to-br from-atlantis to-vida-loca animate-pulse opacity-30 pointer-events-none"></div>
                )}

                <button
                    onClick={toggleWidget}
                    className={`
                        relative w-16 h-16 rounded-full shadow-2xl transition-all duration-500 transform
                        ${isOpen ? 'rotate-180 scale-110' : 'hover:scale-110'}
                        bg-gradient-to-br from-atlantis via-green-yellow-primary to-vida-loca 
                        hover:from-vida-loca hover:via-atlantis hover:to-green-yellow-neon
                        flex items-center justify-center text-white
                        border-4 ${isDark ? 'border-gray-800' : 'border-white'} 
                        hover:border-green-yellow-200
                        focus:outline-none focus:ring-4 focus:ring-atlantis/50
                        backdrop-blur-sm z-20
                    `}
                    aria-label={t.toggle || 'Quick Contact'}
                >
                    {isOpen ? (
                        <FaTimes className="text-2xl transition-transform duration-300 text-black" />
                    ) : (
                        <FaComments className="text-2xl transition-transform duration-300 text-black" />
                    )}
                </button>

                {/* Efecto de ondas cuando está cerrado - DETRÁS del botón para no bloquear clics */}
                {!isOpen && shouldPulse && (
                    <>
                        <div className="absolute inset-0 rounded-full bg-atlantis animate-ping opacity-20 pointer-events-none -z-10"></div>
                        <div className="absolute inset-1 rounded-full bg-green-yellow-primary animate-ping opacity-15 pointer-events-none -z-10" style={{ animationDelay: '0.2s' }}></div>
                        <div className="absolute inset-2 rounded-full bg-vida-loca animate-ping opacity-10 pointer-events-none -z-10" style={{ animationDelay: '0.4s' }}></div>
                    </>
                )}

                {/* Tooltip elegante */}
                {!isOpen && (
                    <div className="absolute bottom-full right-1/2 transform translate-x-1/2 mb-3 opacity-0 group-hover:opacity-100 transition-all duration-300 pointer-events-none">
                        <div className={`
                            ${isDark
                                ? 'bg-gray-900/95 text-white border-gray-700'
                                : 'bg-white/95 text-gray-800 border-green-yellow-200'
                            } 
                            px-4 py-2 rounded-xl text-sm font-medium whitespace-nowrap
                            shadow-2xl border backdrop-blur-sm
                        `}>
                            {t.toggle || 'Quick Contact'}
                        </div>
                        <div className={`
                            w-3 h-3 transform rotate-45 mx-auto -mt-1.5
                            ${isDark ? 'bg-gray-900/95' : 'bg-white/95'}
                        `}></div>
                    </div>
                )}
            </div>

            {/* Panel de opciones elegante */}
            {isOpen && (
                <div className="absolute bottom-20 right-0 animate-slide-up">
                    <div className={`
                        ${isDark
                            ? 'bg-gray-900/95 border-gray-700/50'
                            : 'bg-white/95 border-green-yellow-200/50'
                        } 
                        rounded-3xl shadow-2xl border-2 p-6 min-w-[320px]
                        backdrop-blur-md
                    `}>
                        {/* Header elegante */}
                        <div className="text-center mb-6">
                            <div className="flex items-center justify-center space-x-2 mb-2">
                                <div className="w-8 h-8 rounded-full bg-gradient-to-r from-atlantis to-green-yellow-primary flex items-center justify-center">
                                    <FaComments className="text-white text-sm" />
                                </div>                            <h3 className={`text-xl font-bold ${isDark ? 'text-white' : 'text-saratoga'}`}>
                                    {t.title || 'Contact Us'}
                                </h3>
                            </div>
                            <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-saratoga/70'}`}>
                                {contactData.connectWith || 'Connect with us'}
                            </p>
                        </div>

                        {/* Opciones de contacto */}
                        <div className="space-y-4">
                            {contactOptions.map((option, index) => {
                                const IconComponent = option.icon;
                                return (
                                    <button
                                        key={index}
                                        onClick={option.action}
                                        className={`
                                            w-full flex items-center space-x-4 px-5 py-4 rounded-2xl
                                            ${option.bgColor}
                                            transition-all duration-300 transform hover:scale-105
                                            shadow-lg hover:shadow-xl
                                            group relative overflow-hidden
                                        `}
                                    >
                                        {/* Efecto de brillo al hover */}
                                        <div className="absolute inset-0 bg-white/20 transform translate-x-[-100%] group-hover:translate-x-[100%] transition-transform duration-500"></div>

                                        <div className="relative flex items-center space-x-4 w-full">
                                            <div className="flex-shrink-0">
                                                <IconComponent className={`text-2xl ${option.iconColor} group-hover:scale-110 transition-transform duration-200`} />
                                            </div>
                                            <div className="flex-1 text-left">
                                                <p className="font-semibold text-white text-sm">
                                                    {option.label}
                                                </p>
                                                <p className="text-white/80 text-xs">
                                                    {option.info}
                                                </p>
                                            </div>
                                        </div>
                                    </button>
                                );
                            })}
                        </div>

                        {/* Información adicional elegante */}
                        <div className={`
                            mt-6 pt-4 border-t 
                            ${isDark ? 'border-gray-700' : 'border-green-yellow-200'}
                        `}>
                            <div className="grid grid-cols-1 gap-3">
                                <div className="flex items-center space-x-3">
                                    <div className={`
                                        w-8 h-8 rounded-full flex items-center justify-center
                                        ${isDark ? 'bg-gray-800' : 'bg-green-yellow-100'}
                                    `}>
                                        <FaMapMarkerAlt className={`text-sm ${isDark ? 'text-atlantis' : 'text-saratoga'}`} />
                                    </div>
                                    <div>
                                        <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-saratoga'}`}>
                                            {contactConfig.company.location}
                                        </p>
                                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-saratoga/60'}`}>
                                            {contactData.location || 'Location'}
                                        </p>
                                    </div>
                                </div>
                                <div className="flex items-center space-x-3">
                                    <div className={`
                                        w-8 h-8 rounded-full flex items-center justify-center
                                        ${isDark ? 'bg-gray-800' : 'bg-green-yellow-100'}
                                    `}>
                                        <FaClock className={`text-sm ${isDark ? 'text-atlantis' : 'text-saratoga'}`} />
                                    </div>
                                    <div>
                                        <p className={`text-sm font-medium ${isDark ? 'text-white' : 'text-saratoga'}`}>
                                            {t.mondayToFriday || 'Mon-Fri: 8:00 AM - 6:00 PM'}
                                        </p>
                                        <p className={`text-xs ${isDark ? 'text-gray-400' : 'text-saratoga/60'}`}>
                                            {t.businessHours || 'Business Hours'}
                                        </p>
                                    </div>
                                </div>
                            </div>
                        </div>

                        {/* Badge de respuesta rápida */}
                        <div className="mt-4 text-center">
                            <span className={`
                                inline-flex items-center px-3 py-1 rounded-full text-xs font-medium
                                ${isDark
                                    ? 'bg-atlantis/20 text-atlantis border border-atlantis/30'
                                    : 'bg-atlantis/10 text-atlantis border border-atlantis/20'
                                }
                            `}>
                                ⚡ {t.quickResponse || 'Quick Response'}
                            </span>
                        </div>
                    </div>
                </div>
            )}

            {/* Overlay para cerrar al hacer clic fuera */}
            {isOpen && (
                <div
                    className="fixed inset-0 -z-10 bg-black/10 backdrop-blur-[1px]"
                    onClick={() => setIsOpen(false)}
                />
            )}
        </div>
    );
};

export default FloatingContactWidget;
